import { Controller } from "@hotwired/stimulus";
import { patch } from "@rails/request.js";

// Connects to data-controller="variant-selector"
export default class extends Controller {
  static outlets = ["configurator-state"];

  static values = {
    url: String,
    availableVariants: Array,
    availableOptionTypes: Array,
    availableOptionValues: Array,
  };

  static targets = ["option", "variantIdField"];

  // The configurator can open with a variant already picked (a tile on the
  // product page); the native selects must not offer what it rules out.
  connect() {
    this.updateSubsequentChoices(0, { nativeOnly: true });
  }

  update(event) {
    const changedIndex = this.optionTargets.indexOf(event.target);
    this.resetSubsequentSelects(event);
    this.updateSubsequentChoices(changedIndex);

    const filteredVariants = this.variantsFilteredBySelectedOptions;
    const variantId =
      filteredVariants.length === 1 ? filteredVariants[0].id : null;

    if (this.hasConfiguratorStateOutlet) {
      if (variantId) {
        this.configuratorStateOutlet.variantIdChanged(variantId);
        this.syncUrl(variantId);
      }
    } else if (this.hasUrlValue) {
      this.requestServerUpdate(variantId);
    }
  }

  async requestServerUpdate(variantId) {
    if (variantId) this.syncUrl(variantId);
    await patch(this.urlValue, {
      body: JSON.stringify({ order_item: { variant_id: variantId } }),
      responseKind: "turbo-stream",
    });
  }

  syncUrl(variantId) {
    const url = new URL(window.location.href);
    url.searchParams.set("variant_id", variantId);
    window.history.replaceState({}, "", url);
  }

  resetSubsequentSelects(event) {
    const changedOptionTargetIndex = this.optionTargets.indexOf(event.target);

    this.optionTargets.forEach((optionTarget, index) => {
      if (index > changedOptionTargetIndex) {
        const choicesController =
          this.application.getControllerForElementAndIdentifier(
            optionTarget,
            "choices-select",
          );
        if (choicesController) {
          choicesController.reset();
        } else {
          optionTarget.value = "";
        }
      }
    });
  }

  updateSubsequentChoices(fromIndex, { nativeOnly = false } = {}) {
    this.optionTargets.forEach((optionTarget, index) => {
      // Tile-picked option types ride along as hidden fields; nothing to filter.
      if (index <= fromIndex || optionTarget.tagName !== "SELECT") return;

      const precedingSelections = this.optionTargets
        .slice(0, index)
        .filter((t) => t.value)
        .map((t) => ({
          optionTypeId: Number(t.dataset.optionTypeId),
          optionValueId: Number(t.value),
        }));

      const compatibleVariants = this.availableVariantsValue.filter((variant) =>
        precedingSelections.every((sel) =>
          variant.options.some(
            (opt) =>
              opt.option_type_id === sel.optionTypeId &&
              opt.option_value_id === sel.optionValueId,
          ),
        ),
      );

      const optionTypeId = Number(optionTarget.dataset.optionTypeId);
      const validIds = new Set(
        compatibleVariants
          .flatMap((v) => v.options)
          .filter((opt) => opt.option_type_id === optionTypeId)
          .map((opt) => opt.option_value_id),
      );

      if (this.isNativeSelect(optionTarget)) {
        this.filterNativeOptions(optionTarget, validIds);
      } else if (!nativeOnly) {
        this.application
          .getControllerForElementAndIdentifier(optionTarget, "choices-select")
          ?.filterByIds(validIds);
      }
    });
  }

  // Read from the markup, not from the controller lookup: choices-select may
  // not be connected yet when this controller connects.
  isNativeSelect(select) {
    return !select.matches("[data-controller~='choices-select']");
  }

  // Mobile renders native selects (skip_choices_js) for the OS picker. iOS
  // ignores `hidden` and `display: none` on <option>, so the unavailable ones
  // are taken out of the select and put back from the original list when an
  // earlier pick changes.
  filterNativeOptions(select, validIds) {
    this.nativeOptions ??= new WeakMap();
    if (!this.nativeOptions.has(select)) {
      this.nativeOptions.set(select, Array.from(select.options));
    }

    const selected = select.value;
    select.replaceChildren(
      ...this.nativeOptions
        .get(select)
        .filter((option) => option.value === "" || validIds.has(Number(option.value))),
    );
    select.value = validIds.has(Number(selected)) ? selected : "";
  }

  get selectedOptions() {
    const options = this.optionTargets
      .filter((target) => target.value)
      .map((target) => ({
        optionTypeId: Number(target.dataset.optionTypeId),
        optionValueId: Number(target.value),
      }));

    return options;
  }

  get variantsFilteredBySelectedOptions() {
    return this.availableVariantsValue.filter((variant) =>
      this.selectedOptions.every((sel) =>
        variant.options.some(
          (opt) =>
            opt.option_type_id === sel.optionTypeId &&
            opt.option_value_id === sel.optionValueId,
        ),
      ),
    );
  }
}
